import { useCallback, useMemo } from 'react';

import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { GET_AGENTS_QUERY_KEY } from '@/constant/agents';
import { useHasAccess } from '@/hooks/useHasAccess';
import { promptsApi } from '@/services/prompts';
import { useAuth } from '@/store/auth';
import {
  CreatePromptGroupBody,
  CreatePromptTemplateBody,
  ListPromptTemplatesParams,
  PromptContextResponse,
  PromptContextSubject,
  UpdatePromptGroupBody,
  UpdatePromptTemplateBody,
} from '@/types/prompts';

const KEY = 'prompts';

export const promptsKeys = {
  config: (userId: number | undefined) => [KEY, 'config', userId] as const,
  templates: (params: ListPromptTemplatesParams) =>
    [KEY, 'templates', params] as const,
  groups: [KEY, 'groups'] as const,
  template: (templateId: number) => [KEY, 'template', templateId] as const,
  versions: (templateId: number) => [KEY, 'versions', templateId] as const,
  usage: [KEY, 'usage'] as const,
  recent: (agentId: number | undefined) => [KEY, 'recent', agentId] as const,
  context: (subject: PromptContextSubject | null) =>
    [KEY, 'context', subject?.kind ?? 'none', subject?.id ?? null] as const,
};

const RECENT_LIMIT = 5;

/**
 * The tenant switch for Prompt Templates, read once per signed-in user.
 * Off, or not read yet, means no Prompts page and no sidebar item.
 */
export function usePromptsConfig() {
  const { user } = useAuth();

  return useQuery({
    queryKey: promptsKeys.config(user?.id),
    queryFn: promptsApi.getConfig,
    enabled: Boolean(user?.company_id),
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
  });
}

export function usePromptsEnabled() {
  const { data } = usePromptsConfig();
  return data?.enabled === true;
}

/**
 * Whether the caller may change team prompts and groups (VH3_CONNECT #595):
 * the `edit_prompts` role permission and `prompts/config.can_edit_team` both.
 * Xano enforces it on every team write regardless.
 */
export function useCanEditTeamPrompts() {
  const hasPermission = useHasAccess('edit_prompts');
  const { data } = usePromptsConfig();
  return hasPermission && data?.can_edit_team === true;
}

export function usePromptTemplates(
  params: ListPromptTemplatesParams,
  enabled = true
) {
  return useQuery({
    queryKey: promptsKeys.templates(params),
    queryFn: () => promptsApi.listTemplates(params),
    placeholderData: keepPreviousData,
    enabled,
    retry: false,
  });
}

export function usePromptGroups(enabled = true) {
  return useQuery({
    queryKey: promptsKeys.groups,
    queryFn: promptsApi.listGroups,
    staleTime: 5 * 60 * 1000,
    enabled,
    retry: false,
  });
}

/**
 * Refetches what a template write changes: the Prompts page lists and every
 * agent's `/` menu (the commands endpoint, `[GET_AGENTS_QUERY_KEY, agentId]`
 * in `useAgentPromptCommands`). The agents list itself is left alone.
 */
export function useRefreshPrompts() {
  const queryClient = useQueryClient();
  return useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: [KEY, 'templates'] }),
        queryClient.invalidateQueries({ queryKey: [KEY, 'recent'] }),
        queryClient.invalidateQueries({
          predicate: ({ queryKey }) =>
            queryKey[0] === GET_AGENTS_QUERY_KEY && queryKey.length === 2,
        }),
      ]),
    [queryClient]
  );
}

/**
 * After a group change: the groups list, the template lists (their group
 * refs) and every agent's `/` menu, so a new group shows with its drill-in word.
 */
export function useRefreshPromptGroups() {
  const queryClient = useQueryClient();
  const refresh = useRefreshPrompts();
  return useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: promptsKeys.groups }),
        refresh(),
      ]),
    [queryClient, refresh]
  );
}

/** A new personal template; the page and the menus refresh on success. */
export function useCreatePromptTemplate() {
  const refresh = useRefreshPrompts();
  return useMutation({
    mutationFn: (body: CreatePromptTemplateBody) =>
      promptsApi.createTemplate(body),
    onSuccess: () => refresh(),
  });
}

/** An edit (a new Template Version); the page and the menus refresh on success. */
export function useUpdatePromptTemplate() {
  const refresh = useRefreshPrompts();
  return useMutation({
    mutationFn: ({
      templateId,
      body,
    }: {
      templateId: number;
      body: UpdatePromptTemplateBody;
    }) => promptsApi.updateTemplate(templateId, body),
    onSuccess: () => refresh(),
  });
}

/**
 * Archive (`archived: true`) or restore a template. The caller refreshes with
 * `useRefreshPrompts` once its note has shown, so the row doesn't vanish
 * under the note.
 */
export function useSetPromptTemplateArchived() {
  return useMutation({
    mutationFn: ({
      templateId,
      archived,
    }: {
      templateId: number;
      archived: boolean;
    }) =>
      archived
        ? promptsApi.archiveTemplate(templateId)
        : promptsApi.unarchiveTemplate(templateId),
  });
}

/**
 * The tenant's copy of a VH3 default. The caller refreshes once the editor
 * is open on the copy.
 */
export function useCopyPromptTemplate() {
  return useMutation({
    mutationFn: (templateId: number) => promptsApi.copyTemplate(templateId),
  });
}

/**
 * Hide or unhide a VH3 default for the tenant. Like archive, the caller
 * refreshes once its note has shown.
 */
export function useSetPromptTemplateHidden() {
  return useMutation({
    mutationFn: ({
      templateId,
      hidden,
    }: {
      templateId: number;
      hidden: boolean;
    }) => promptsApi.setTemplateHidden(templateId, hidden),
  });
}

/** A new group, or an edit (a VH3 group's edit is the tenant's restyle). */
export function useSavePromptGroup() {
  const refresh = useRefreshPromptGroups();
  return useMutation({
    mutationFn: (
      input:
        | { groupId: number; body: UpdatePromptGroupBody }
        | { groupId?: undefined; body: CreatePromptGroupBody }
    ) =>
      input.groupId !== undefined
        ? promptsApi.updateGroup(input.groupId, input.body)
        : promptsApi.createGroup(input.body as CreatePromptGroupBody),
    onSuccess: () => refresh(),
  });
}

/** Hide or unhide a VH3 group, or archive a team group. */
export function useChangePromptGroup() {
  const refresh = useRefreshPromptGroups();
  return useMutation({
    mutationFn: ({
      groupId,
      change,
    }: {
      groupId: number;
      change: 'hide' | 'unhide' | 'archive';
    }): Promise<unknown> =>
      change === 'archive'
        ? promptsApi.archiveGroup(groupId)
        : promptsApi.setGroupHidden(groupId, change === 'hide'),
    onSuccess: () => refresh(),
  });
}

/** One template the caller can see, e.g. the VH3 original of a team copy. */
export function usePromptTemplate(templateId: number, enabled = true) {
  return useQuery({
    queryKey: promptsKeys.template(templateId),
    queryFn: () => promptsApi.getTemplate(templateId),
    enabled: enabled && templateId > 0,
    retry: false,
  });
}

/** The VH3 originals of several team copies, one query each. */
export function usePromptOrigins(templateIds: number[]) {
  return useQueries({
    queries: templateIds.map(templateId => ({
      queryKey: promptsKeys.template(templateId),
      queryFn: () => promptsApi.getTemplate(templateId),
      retry: false,
    })),
  });
}

/** A template's versions, newest first (#596). */
export function usePromptVersions(templateId: number, enabled = true) {
  return useQuery({
    queryKey: promptsKeys.versions(templateId),
    queryFn: () => promptsApi.listVersions(templateId),
    enabled: enabled && templateId > 0,
    retry: false,
  });
}

/**
 * Restore an old version as a new one. The versions and the lists (the
 * caption's "Saved vN") refresh on success.
 */
export function useRestorePromptVersion() {
  const queryClient = useQueryClient();
  const refresh = useRefreshPrompts();
  return useMutation({
    mutationFn: ({
      templateId,
      version,
    }: {
      templateId: number;
      version: number;
    }) => promptsApi.restoreVersion(templateId, version),
    onSuccess: (_saved, { templateId }) =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: promptsKeys.versions(templateId),
        }),
        refresh(),
      ]),
  });
}

/**
 * Take update or Keep mine on a team copy of a VH3 default. The caller
 * refreshes once the notice has folded away, so the card doesn't vanish
 * under its own confirmation.
 */
export function useAnswerPromptUpdate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      templateId,
      answer,
    }: {
      templateId: number;
      answer: 'take' | 'keep';
    }): Promise<unknown> =>
      answer === 'take'
        ? promptsApi.takeUpdate(templateId)
        : promptsApi.keepMine(templateId),
    onSuccess: (_result, { templateId }) =>
      queryClient.invalidateQueries({
        queryKey: promptsKeys.versions(templateId),
      }),
  });
}

/**
 * Prompt Use counts by template, read once per page load (and again after a
 * send that carried templates).
 */
export function usePromptUsage(enabled = true) {
  const query = useQuery({
    queryKey: promptsKeys.usage,
    queryFn: promptsApi.listUsage,
    enabled,
    staleTime: Infinity,
    retry: false,
  });
  const byTemplate = useMemo(
    () => new Map((query.data ?? []).map(item => [item.template_id, item])),
    [query.data]
  );
  return { ...query, byTemplate };
}

/** The caller's last 5 templates for one agent, for the menus' Recent row. */
export function useRecentPrompts(agentId: number | undefined, enabled = true) {
  return useQuery({
    queryKey: promptsKeys.recent(agentId),
    queryFn: () => promptsApi.listRecent(agentId!, RECENT_LIMIT),
    enabled: enabled && !!agentId,
    staleTime: 30 * 1000,
    retry: false,
  });
}

/**
 * After a send that carried templates: Xano wrote Prompt Use rows, so the
 * Recent rows and the usage counts refetch. The send paths call it on
 * success; their requests stay as they are.
 */
export function useRecordPromptUse() {
  const queryClient = useQueryClient();
  return useCallback(
    () =>
      Promise.all([
        queryClient.invalidateQueries({ queryKey: [KEY, 'recent'] }),
        queryClient.invalidateQueries({ queryKey: promptsKeys.usage }),
      ]),
    [queryClient]
  );
}

const EMPTY_CONTEXT: PromptContextResponse = {
  subject: null,
  names: [],
  values: {},
};

/**
 * Context Variables for one chat (spec FSI #1230): one lookup when the chat
 * opens and again when its subject changes, cached per subject. With the
 * switch off there is no call. A failed or pending lookup counts as nothing
 * found, so callers always get a response and nothing is blocked.
 */
export function usePromptContext(
  subject: PromptContextSubject | null,
  enabled = true
) {
  const promptsEnabled = usePromptsEnabled();
  const query = useQuery({
    queryKey: promptsKeys.context(subject),
    queryFn: () => promptsApi.getContext(subject),
    enabled: enabled && promptsEnabled,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
  return {
    ...query,
    context: query.data ?? EMPTY_CONTEXT,
  };
}
